import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { detectTimezoneViaIP } from '@/services/geolocation-service';
import {
  formatTimestamp,
  isValidTimezone,
  type TimestampFormat,
} from '@/utils/timezone';

interface TimezoneContextType {
  userTimezone: string;
  isTimezoneLoaded: boolean;
  formatUserTimestamp: (
    utcTimestamp: string | Date,
    format?: TimestampFormat
  ) => string;
  getCurrentUserTimestamp: (format?: TimestampFormat) => string;
  /** Manually update the timezone (e.g., after profile settings change) */
  updateTimezone: (newTimezone: string) => void;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(
  undefined
);

interface TimezoneProviderProps {
  children: React.ReactNode;
  profileTimezone?: string | null;
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({
  children,
  profileTimezone,
}) => {
  const [userTimezone, setUserTimezone] = useState<string>('UTC');
  const [isTimezoneLoaded, setIsTimezoneLoaded] = useState<boolean>(false);
  const [isDetecting, setIsDetecting] = useState<boolean>(false);
  // Track the last manually set timezone to prevent effect from overriding it
  const lastManualTimezoneRef = useRef<string | null>(null);

  // Detect timezone using the hierarchy: User Profile -> Browser -> IP Geolocation
  const detectTimezone = async (): Promise<string> => {
    // 1. Try user profile timezone first
    if (profileTimezone && isValidTimezone(profileTimezone)) {
      return profileTimezone;
    }

    // 2. Try browser timezone detection
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (isValidTimezone(browserTimezone)) {
        return browserTimezone;
      }
    } catch (error) {
      console.warn('Failed to detect browser timezone:', error);
    }

    // 3. Try IP-based geolocation as fallback
    const ipTimezone = await detectTimezoneViaIP();
    if (ipTimezone) {
      return ipTimezone;
    }

    // 4. Final fallback to UTC (should rarely happen)
    console.warn('All timezone detection methods failed, falling back to UTC');
    return 'UTC';
  };

  // Clear manual update ref when user profile timezone matches the manual update
  useEffect(() => {
    if (
      lastManualTimezoneRef.current &&
      profileTimezone === lastManualTimezoneRef.current
    ) {
      // User profile now matches our manual update, clear the ref
      lastManualTimezoneRef.current = null;
    }
  }, [profileTimezone]);

  // Load user timezone with the new detection hierarchy
  useEffect(() => {
    const loadTimezone = async () => {
      if (isDetecting) return; // Prevent multiple simultaneous detections

      // Don't override if we just manually set this timezone
      if (
        lastManualTimezoneRef.current &&
        lastManualTimezoneRef.current === userTimezone
      ) {
        return;
      }

      setIsDetecting(true);
      try {
        const detectedTimezone = await detectTimezone();
        setUserTimezone(detectedTimezone);
        // Only update if it's different from current and not a manual update we're preserving
        if (
          detectedTimezone !== userTimezone &&
          detectedTimezone !== lastManualTimezoneRef.current
        ) {
          setUserTimezone(detectedTimezone);
        }
        setIsTimezoneLoaded(true);
      } catch (error) {
        console.error('Timezone detection failed:', error);
        setUserTimezone('UTC');
        if (userTimezone === 'UTC' || !lastManualTimezoneRef.current) {
          setUserTimezone('UTC');
        }
        setIsTimezoneLoaded(true);
      } finally {
        setIsDetecting(false);
      }
    };

    // Only run detection if timezone is not loaded or user profile changed
    if (
      !isTimezoneLoaded ||
      (profileTimezone && profileTimezone !== userTimezone)
    ) {
      loadTimezone();
    }
  }, [profileTimezone, isTimezoneLoaded, userTimezone, isDetecting]);

  // Helper function to format timestamps using user's timezone
  const formatUserTimestamp = (
    utcTimestamp: string | Date,
    format: TimestampFormat = 'full'
  ): string => {
    return formatTimestamp(utcTimestamp, userTimezone, format);
  };

  // Helper function to get current timestamp in user's timezone
  const getCurrentUserTimestamp = (
    format: TimestampFormat = 'full'
  ): string => {
    return formatTimestamp(new Date(), userTimezone, format);
  };

  // Function to manually update the timezone (e.g., after profile settings change)
  const updateTimezone = (newTimezone: string): void => {
    if (newTimezone && isValidTimezone(newTimezone)) {
      setUserTimezone(newTimezone);
      // Track this as a manual update to prevent effect from overriding it
      lastManualTimezoneRef.current = newTimezone;
    }
  };

  const value: TimezoneContextType = {
    userTimezone,
    isTimezoneLoaded,
    formatUserTimestamp,
    getCurrentUserTimestamp,
    updateTimezone,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = (): TimezoneContextType => {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

// Export the context for testing purposes
export { TimezoneContext };
